import { Component, computed } from '@angular/core';
import { BlockAbstractComponent } from '@gallaghercp/core-frontend';

type ResponsiveValue<T> = T | { mobile?: T; tablet?: T; desktop?: T };

@Component({
    selector: 'core-block-content-hero-banner',
    templateUrl: './block-content-hero-banner.component.html',
    standalone: false,
})
export class BlockContentHeroBannerComponent extends BlockAbstractComponent {
    contentHeroConfig = computed(() => this.block.data.contentHeroBannerConfig);

    contentHeroStyles = computed(() => {
        const contentContainerPadding = this.createResponsiveCssVars(
            '--hero-content-container-padding',
            this.contentHeroConfig()?.contentContainer?.padding,
        );

        const headingTypography = this.contentHeroConfig()?.typography?.heading;
        const headingFontSizes = this.createResponsiveCssVars('--hero-heading-font-size', headingTypography?.fontSize);
        const headingFontWeight = this.createResponsiveCssVars(
            '--hero-heading-font-weight',
            headingTypography?.fontWeight,
        );
        const headingLineHeight = this.createResponsiveCssVars(
            '--hero-heading-line-height',
            headingTypography?.lineHeight,
        );
        const headingLetterSpacing = this.createResponsiveCssVars(
            '--hero-heading-letter-spacing',
            headingTypography?.letterSpacing,
        );

        const descriptionTypography = this.contentHeroConfig()?.typography?.description;
        const descriptionFontSizes = this.createResponsiveCssVars(
            '--hero-description-font-size',
            descriptionTypography?.fontSize,
        );
        const descriptionFontWeight = this.createResponsiveCssVars(
            '--hero-description-font-weight',
            descriptionTypography?.fontWeight,
        );
        const descriptionLineHeight = this.createResponsiveCssVars(
            '--hero-description-line-height',
            descriptionTypography?.lineHeight,
        );
        const descriptionLetterSpacing = this.createResponsiveCssVars(
            '--hero-description-letter-spacing',
            descriptionTypography?.letterSpacing,
        );

        return {
            ...contentContainerPadding,

            '--hero-border-radius': this.contentHeroConfig()?.container?.borderRadius,
            '--hero-min-height': this.block.data.bannerMinHeight,

            ...headingFontSizes,
            ...headingFontWeight,
            ...headingLineHeight,
            ...headingLetterSpacing,

            ...descriptionFontSizes,
            ...descriptionFontWeight,
            ...descriptionLineHeight,
            ...descriptionLetterSpacing,
        };
    });

    hasCustomHeadingTypography = computed(() =>
        Object.values(this.contentHeroConfig()?.typography?.heading ?? {}).some(Boolean),
    );
    hasCustomDescriptionTypography = computed(() =>
        Object.values(this.contentHeroConfig()?.typography?.description ?? {}).some(Boolean),
    );

    private getResponsiveValue(value?: string | ResponsiveValue<string>): {
        mobile?: string;
        tablet?: string;
        desktop?: string;
    } {
        if (typeof value === 'string')
            return {
                mobile: value,
                tablet: value,
                desktop: value,
            };

        return value ?? {};
    }

    private createResponsiveCssVars(prefix: string, values?: ResponsiveValue<string>) {
        const responsiveValue = this.getResponsiveValue(values);

        return {
            [`${prefix}--mobile`]: responsiveValue.mobile,
            [`${prefix}--tablet`]: responsiveValue.tablet,
            [`${prefix}--desktop`]: responsiveValue.desktop,
        };
    }
}
